import type { LoggerConfig, LoggerColors } from './_types';

/**
 * Default logger configuration
 */
export const DEFAULT_CONFIG: LoggerConfig = {
  level: 'off', // Default to off for production
  timestamps: true,
  showComponent: true,
  showFile: false,
  prefix: '',
  colors: true,
};

/**
 * Get logger configuration from storage
 */
export const getStoredConfig = async (): Promise<Partial<LoggerConfig>> => {
  try {
    // Dynamic import to avoid circular dependency
    const { StorageHelper } = await import('~/common/helpers');
    const stored = StorageHelper.getItem(StorageHelper.KEY.LOGGER_CONFIG);
    return stored || {};
  } catch (error) {
    console.warn('Failed to load logger config from storage:', error);
    return {};
  }
};

/**
 * Save logger configuration to storage
 */
export const saveConfigToStorage = async (config: LoggerConfig): Promise<void> => {
  try {
    // Dynamic import to avoid circular dependency
    const { StorageHelper } = await import('~/common/helpers');
    StorageHelper.setItem(StorageHelper.KEY.LOGGER_CONFIG, config);
  } catch (error) {
    console.warn('Failed to save logger config to storage:', error);
  }
};

/**
 * Log level priorities (higher = more important)
 */
export const LOG_LEVELS: Record<string, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  off: 999,
};

/**
 * Color schemes for different log levels
 */
export const COLORS: LoggerColors = {
  debug: '#6B7280', // Gray
  info: '#3B82F6', // Blue
  warn: '#F59E0B', // Amber
  error: '#EF4444', // Red
  timestamp: '#9CA3AF', // Light gray
  component: '#8B5CF6', // Purple
  file: '#059669', // Emerald
};

/**
 * Log level emojis for better visual identification
 */
export const LEVEL_EMOJIS = {
  debug: '🔍',
  info: 'ℹ️',
  warn: '⚠️',
  error: '❌',
} as const;

/**
 * Available log modes that can be set from browser console
 */
export const LOG_MODES = {
  debug: { level: 'debug', timestamps: true, showComponent: true, showFile: true },
  info: { level: 'info', timestamps: true, showComponent: true, showFile: false },
  warn: { level: 'warn', timestamps: false, showComponent: true, showFile: false },
  error: { level: 'error', timestamps: false, showComponent: false, showFile: false },
  off: { level: 'off', timestamps: false, showComponent: false, showFile: false },
} as const;
